import dayjs from "dayjs";

/**
 * NZSC-4010 리포트 열람 전 잔금일 "확인" 퍼널 — 화면·모달 판정을 컴포넌트에서 분리한 순수 함수.
 *
 * `GET /api/v1/report/user/:document` 응답의 잔금 필드:
 *   balanceDate            정확한 날짜(datetime 직렬화 또는 YYYYMMDD)
 *   balancePeriodYm/Part   초·중·하순("YYYYMM", EARLY|MID|LATE)
 *   balanceFinalType       'DATE' | 'PERIOD' | null — 둘 다 저장돼 있을 수 있으므로 표시·판정은 항상 이 값 기준
 *   balanceConfirmDate     열람 전 확인 완료 시각(null이면 아직 확인 전) — DB `_date` 규약
 *   subscriptionStatus     'active' | 'scheduled' | 'none' (프리미엄 구독 예약 모델)
 *   subscriptionActivateAt 구독 활성화 예정 시각
 *
 * 선례: components/common/balancePeriodOptions.js (.vue 안에 두면 테스트로 못 잡는다)
 */

export const PART_LABELS = { EARLY: "초순", MID: "중순", LATE: "하순" };

export const BALANCE_FINAL_TYPE = { DATE: "DATE", PERIOD: "PERIOD" };

/** 서버 잔금일(datetime ISO · "YYYYMMDD" · "YYYY-MM-DD") → "YYYY-MM-DD". 없으면 null */
export function normalizeBalanceDate(value) {
  if (!value) return null;
  const str = String(value).trim();
  if (/^\d{8}$/.test(str)) return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const d = dayjs(str);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
}

/** 서버 초·중·하순(ym, part) → { year, month, ym, part }. 없으면 null */
export function normalizeBalancePeriod(ym, part) {
  if (!ym || !part || !PART_LABELS[part]) return null;
  const s = String(ym);
  if (!/^\d{6}$/.test(s)) return null;
  return { year: parseInt(s.slice(0, 4), 10), month: parseInt(s.slice(4, 6), 10), ym: s, part };
}

/**
 * report 응답 → 잔금 일정 스냅샷.
 * finalType 이 없으면(배포 전 신청 건) hasSaved=false → 기존 입력 퍼널 폴백.
 */
export function resolveBalanceSchedule(report) {
  const r = report || {};
  const finalType =
    r.balanceFinalType === BALANCE_FINAL_TYPE.DATE ||
    r.balanceFinalType === BALANCE_FINAL_TYPE.PERIOD
      ? r.balanceFinalType
      : null;
  const date = normalizeBalanceDate(r.balanceDate);
  const period = normalizeBalancePeriod(r.balancePeriodYm, r.balancePeriodPart);
  // 최종값 종류는 있는데 그 값이 비어 있으면 저장값 없음으로 본다(서버 불일치 방어 — 미입력 취급이 아니라 폴백)
  const hasSaved =
    (finalType === BALANCE_FINAL_TYPE.DATE && !!date) ||
    (finalType === BALANCE_FINAL_TYPE.PERIOD && !!period);
  return {
    finalType: hasSaved ? finalType : null,
    date,
    period,
    hasSaved,
    isConfirmed: !!r.balanceConfirmDate,
    confirmDate: r.balanceConfirmDate || null,
    subscriptionStatus: r.subscriptionStatus || null,
    subscriptionActivateAt: r.subscriptionActivateAt || null,
  };
}

/** 최종값 기준 짧은 표기 — "10월 26일" / "10월 중순" (제목·CTA용) */
export function formatBalanceShort({ finalType, date, period }) {
  if (finalType === BALANCE_FINAL_TYPE.DATE && date) return dayjs(date).format("M월 D일");
  if (finalType === BALANCE_FINAL_TYPE.PERIOD && period) {
    return `${period.month}월 ${PART_LABELS[period.part]}`;
  }
  return "";
}

/** 조사 '로/으로' — 받침 없음·ㄹ받침이면 "로", 그 외 "으로" ("26일로" / "중순으로", Figma 3790-789/809) */
export function withRoParticle(word) {
  if (!word) return "";
  const code = word.charCodeAt(word.length - 1);
  if (code < 0xac00 || code > 0xd7a3) return `${word}로`;
  const jong = (code - 0xac00) % 28;
  return jong === 0 || jong === 8 ? `${word}로` : `${word}으로`;
}

/** 최종값 기준 연도 포함 표기 — "2026년 10월 26일" / "2026년 10월 중순" (입력 필드용) */
export function formatBalanceFull({ finalType, date, period }) {
  if (finalType === BALANCE_FINAL_TYPE.DATE && date) return dayjs(date).format("YYYY년 M월 D일");
  if (finalType === BALANCE_FINAL_TYPE.PERIOD && period) {
    return `${period.year}년 ${period.month}월 ${PART_LABELS[period.part]}`;
  }
  return "";
}

/**
 * 리포트 진입 분기 (일반·프리미엄 공통).
 *   confirm  — 저장값 있음 & 확인 전 → 확인 퍼널
 *   done     — 저장값 있음 & 확인 완료 → 기존 재진입 정책(호출측)
 *   fallback — 저장값 없음(배포 전 신청) → 기존 입력 퍼널
 * report 미도착(null/빈 객체·reportSeq 없음)이면 null — 어떤 분기도 실행하지 않는다.
 */
export function resolveReportBalanceEntry(report) {
  if (!report || !report.reportSeq) return null;
  const s = resolveBalanceSchedule(report);
  if (!s.hasSaved) return "fallback";
  return s.isConfirmed ? "done" : "confirm";
}

/**
 * NZSC-4010 테스터 오픈 게이트를 거친 진입 판정. 게이트 꺼짐(일반 고객)은 절대 "confirm" 을 돌려주지 않는다 —
 * 폴백 입력·직거래 설문 저장은 balance_final_type 을 남기므로(서버 비게이트) 데이터만으로는 확인 퍼널로 새어 들어간다(CR C).
 * 꺼짐: 저장값 있음 → "done"(배포 전과 같이 아무것도 안 띄움·최종값 기준 배너) / 없음 → "fallback".
 */
export function resolveReportBalanceEntryGated(report, gateOn) {
  const entry = resolveReportBalanceEntry(report);
  if (gateOn || entry !== "confirm") return entry;
  return "done";
}

/**
 * 프리미엄 "다음"/저장 시 확인 팝업 판정 (6개월 기준 폐지).
 * BE 계약(PremiumSubscribeSchedule.judgePremiumSubscribeStart)과 같은 식:
 *   start = 잔금일 − 1개월(startOf day). start > 오늘 → 예약(팝업) / start ≤ 오늘 → 즉시 활성화(팝업 없음)
 *   DATE & start > 오늘 → { mode:'date', date, monitorStart(=start) }
 *   PERIOD             → { mode:'period', ...period }
 *   DATE & start ≤ 오늘 → null (팝업 없음)
 * "정확히 오늘+1개월"은 start == 오늘이라 팝업 없음. 월 경계도 같은 식으로만 판정한다
 * (오늘 01-31·잔금 02-28 → start 01-28 ≤ 오늘 → 없음 / 오늘 01-30·잔금 03-01 → start 02-01 > 오늘 → 팝업.
 *  dayjs subtract 는 03-31 − 1개월 = 02-28 처럼 말일로 클램프한다).
 */
export function resolveBalanceConfirmModal({ finalType, date, period }, today = dayjs()) {
  if (finalType === BALANCE_FINAL_TYPE.PERIOD && period) {
    return { mode: "period", year: period.year, month: period.month, part: period.part };
  }
  if (finalType === BALANCE_FINAL_TYPE.DATE && date) {
    const start = dayjs(date).subtract(1, "month").startOf("day");
    if (!start.isAfter(dayjs(today).startOf("day"))) return null;
    return { mode: "date", date, monitorStart: start.format("YYYY-MM-DD") };
  }
  return null;
}

/**
 * 확인 화면에서 달력으로 고른 값 → PATCH body. 반대 종류의 이전 값은 서버가 보관하므로
 * 새로 고른 종류의 값 + balance_final_type 만 보낸다.
 */
export function buildBalanceSaveBody(selection) {
  if (selection.type === "date") {
    return {
      balance_date: String(selection.date).replace(/-/g, ""),
      balance_final_type: BALANCE_FINAL_TYPE.DATE,
    };
  }
  return {
    balance_period_ym: selection.ym,
    balance_period_part: selection.part,
    balance_final_type: BALANCE_FINAL_TYPE.PERIOD,
  };
}

/**
 * R12 뒤로가기 — 확인/입력 화면은 리포트에서 `$router.replace` 로 들어오므로 히스토리상 바로 아래가
 * "리포트 진입 직전 화면"이다. 리포트 페이지는 진입 시(beforeRouteEnter) 직전 라우트를 여기 남기고,
 * 확인 화면은 값이 있으면 go(-1), 없으면(새로고침·딥링크 직진입 = 앱 내 직전 화면 없음) 마이페이지로 간다.
 */
export const BALANCE_ENTRY_FROM_KEY = "nz_balance_entry_from";

/**
 * 확인 퍼널 → 계약유형 화면으로 넘길 때 붙이는 query. 계약유형 화면은 이 값이 있을 때만 리포트로 replace 한다
 * (폴백 레거시 입력 퍼널은 기존 push 유지).
 */
export const BALANCE_CONFIRM_ENTRY_QUERY = { key: "entry", value: "confirm" };

/** 리포트 진입 직전 라우트 기록. from 이 앱 밖(START)·리포트 자신·잔금 퍼널이면 "없음"으로 기록한다 */
export function rememberBalanceEntryFrom(from, storage = window.sessionStorage) {
  try {
    const path = from && from.matched && from.matched.length ? from.fullPath : "";
    const isFunnel =
      !path ||
      path.startsWith("/report/buy-renewed") ||
      // 업그레이드 결제 완료 화면은 2초 뒤 리포트로 자동 push 한다 — 여기로 돌아가면 다시 확인 화면으로 밀려온다
      path.startsWith("/report/buy-premium-upgrade/complete") ||
      path.startsWith("/report/buy/balance-date") ||
      path.startsWith("/report/buy/contract-type") ||
      path.startsWith("/premium-report/balance-date");
    if (isFunnel) storage.removeItem(BALANCE_ENTRY_FROM_KEY);
    else storage.setItem(BALANCE_ENTRY_FROM_KEY, path);
  } catch (e) {
    // sessionStorage 불가(프라이빗 모드 등)면 히스토리 뒤로가기로만 동작
  }
}

/** 'history' = go(-1) / 'mypage' = 앱 내 직전 화면 없음 */
export function resolveBalanceBackAction(storage = window.sessionStorage) {
  try {
    return storage.getItem(BALANCE_ENTRY_FROM_KEY) ? "history" : "mypage";
  } catch (e) {
    return "history";
  }
}
